// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/main/ai/speech-to-text.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Speech to text for the assistant's mic (docs/AI_CHATBOT_PLAN.md, phase 5).
 * POST {baseUrl}/audio/transcriptions, multipart `file` + `language`: OpenAI's
 * shape, which the AI gateway (Workers AI Whisper) and local Whisper servers
 * both speak, so switching to a local model is configuration only. The key
 * stays in main, as for chat.
 */
import type { AiChatErrorCode } from '../../shared/ai-chat-protocol';
import type { AiChatLanguage } from '../../shared/ai-languages';
import { ChatEndpointError, errorForStatus, type ChatEndpoint } from './openai-chat-client';

export const AI_SPEECH_TIMEOUT_MS = 30_000;

export type TranscriptionResult = { ok: true; text: string } | { ok: false; code: AiChatErrorCode; message: string };

export function transcriptionsUrl(baseUrl: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/audio/transcriptions`;
}

export async function transcribeSpeech(options: {
  endpoint: ChatEndpoint;
  audio: Uint8Array;
  language: AiChatLanguage;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}): Promise<TranscriptionResult> {
  const form = new FormData();
  form.append('file', new Blob([new Uint8Array(options.audio)], { type: 'audio/wav' }), 'question.wav');
  form.append('language', options.language);
  form.append('model', 'whisper-1');
  const headers: Record<string, string> = {};
  if (options.endpoint.apiKey) headers.Authorization = `Bearer ${options.endpoint.apiKey}`;

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), options.timeoutMs ?? AI_SPEECH_TIMEOUT_MS);
  try {
    const response = await (options.fetchImpl ?? fetch)(transcriptionsUrl(options.endpoint.baseUrl), {
      method: 'POST',
      headers,
      body: form,
      signal: abort.signal
    });
    if (!response.ok) {
      const error = errorForStatus(response.status, await response.text().catch(() => ''));
      return { ok: false, code: error.code, message: error.message };
    }
    const body = await response.json() as { text?: unknown };
    const text = typeof body.text === 'string' ? body.text.trim() : '';
    return text ? { ok: true, text } : { ok: false, code: 'AI_FAILED', message: 'No speech was recognised.' };
  } catch (error) {
    if (abort.signal.aborted) return { ok: false, code: 'AI_TIMEOUT', message: 'Transcription took too long.' };
    if (error instanceof ChatEndpointError) return { ok: false, code: error.code, message: error.message };
    return { ok: false, code: 'AI_UNAVAILABLE', message: 'The speech endpoint cannot be reached.' };
  } finally {
    clearTimeout(timer);
  }
}
